import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getEffectiveAccountId, getCurrentUser } from './authorizationService';

/**
 * Account-Scoped Data Service
 * Handles data operations scoped to the effective account (client account when admin is scoped)
 */

/**
 * Get the effective user ID for data operations
 * For CLIENT users: returns their own UID
 * For ADMIN users: returns the effective account ID (client account they're scoped to)
 * @returns {string|null} Effective user ID for data operations
 */
export const getEffectiveUserId = () => {
  const currentUser = getCurrentUser();
  if (!currentUser) return null;
  
  // For admins scoped to a client account, use the account ID as the effective user ID
  // This ensures data is scoped to the client account, not the admin
  const effectiveAccountId = getEffectiveAccountId();
  if (effectiveAccountId) {
    return effectiveAccountId;
  }
  
  // For regular clients, use their own UID
  return currentUser.uid;
};

/**
 * Get account-scoped reminders
 * @returns {Promise<Array>} Array of reminders
 */
export const getAccountScopedReminders = async () => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    const remindersRef = collection(db, 'reminders');
    const q = query(remindersRef, where('accountId', '==', effectiveUserId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting account-scoped reminders:', error);
    throw error;
  }
};

/**
 * Add account-scoped reminder
 * @param {Object} reminder - Reminder data
 * @returns {Promise<string>} Reminder ID
 */
export const addAccountScopedReminder = async (reminder) => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    const reminderData = {
      ...reminder,
      accountId: effectiveUserId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Use setDoc with the client-provided ID instead of addDoc
    const docRef = doc(db, 'reminders', reminder.id);
    await setDoc(docRef, reminderData);
    return reminder.id;
  } catch (error) {
    console.error('Error adding account-scoped reminder:', error);
    throw error;
  }
};

/**
 * Update account-scoped reminder
 * @param {string} reminderId - Reminder ID
 * @param {Object} updateData - Update data
 * @returns {Promise<void>}
 */
export const updateAccountScopedReminder = async (reminderId, updateData) => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    const reminderRef = doc(db, 'reminders', reminderId);
    // Use setDoc with merge to create if doesn't exist, update if it does
    await setDoc(reminderRef, {
      ...updateData,
      accountId: effectiveUserId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating account-scoped reminder:', error);
    throw error;
  }
};

/**
 * Delete account-scoped reminder
 * @param {string} reminderId - Reminder ID
 * @returns {Promise<void>}
 */
export const deleteAccountScopedReminder = async (reminderId) => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    const reminderRef = doc(db, 'reminders', reminderId);
    await deleteDoc(reminderRef);
  } catch (error) {
    console.error('Error deleting account-scoped reminder:', error);
    throw error;
  }
};

/**
 * Get account-scoped Google Services connections
 * @returns {Promise<Object>} Google Services connection data
 */
export const getAccountScopedGoogleServices = async () => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    const servicesRef = collection(db, 'googleServices');
    const q = query(servicesRef, where('accountId', '==', effectiveUserId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        isConnected: false,
        analytics: null,
        searchConsole: null,
        places: null
      };
    }

    const servicesDoc = querySnapshot.docs[0];
    return {
      id: servicesDoc.id,
      isConnected: true,
      ...servicesDoc.data()
    };
  } catch (error) {
    console.error('Error getting account-scoped Google Services:', error);
    throw error;
  }
};

/**
 * Save account-scoped Google Services connections
 * @param {Object} servicesData - Google Services data
 * @returns {Promise<string>} Services document ID
 */
export const saveAccountScopedGoogleServices = async (servicesData) => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    // Check if services already exist for this account
    const existingServices = await getAccountScopedGoogleServices();
    
    const servicesDataWithAccount = {
      ...servicesData,
      accountId: effectiveUserId,
      updatedAt: serverTimestamp()
    };

    if (existingServices.isConnected) {
      // Update existing services
      const servicesRef = doc(db, 'googleServices', existingServices.id);
      await updateDoc(servicesRef, servicesDataWithAccount);
      return existingServices.id;
    } else {
      // Create new services document
      const servicesDataWithTimestamps = {
        ...servicesDataWithAccount,
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'googleServices'), servicesDataWithTimestamps);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving account-scoped Google Services:', error);
    throw error;
  }
};

/**
 * Get account-scoped user profile data
 * @returns {Promise<Object>} User profile data
 */
export const getAccountScopedUserProfile = async () => {
  try {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    // For admins scoped to client accounts, get the client's profile
    // For regular clients, get their own profile
    const currentUser = getCurrentUser();
    if (currentUser.role === 'ADMIN' && getEffectiveAccountId()) {
      // Admin scoped to client account - get client profile
      const accountRef = doc(db, 'accounts', effectiveUserId);
      const accountDoc = await getDoc(accountRef);
      
      if (!accountDoc.exists()) {
        throw new Error('Client account not found');
      }
      
      return {
        id: accountDoc.id,
        type: 'account',
        name: accountDoc.data().name,
        description: accountDoc.data().description,
        email: `Account: ${accountDoc.data().name}`,
        displayName: accountDoc.data().name,
        photoURL: null,
        isAdminScoped: true
      };
    } else {
      // Regular client - get their own profile
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User profile not found');
      }
      
      return {
        id: userDoc.id,
        type: 'user',
        ...userDoc.data(),
        isAdminScoped: false
      };
    }
  } catch (error) {
    console.error('Error getting account-scoped user profile:', error);
    throw error;
  }
};

/**
 * Update account-scoped user profile
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<void>}
 */
export const updateAccountScopedUserProfile = async (profileData) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No current user');
    }

    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      throw new Error('No effective user ID available');
    }

    if (currentUser.role === 'ADMIN' && getEffectiveAccountId()) {
      // Admin scoped to client account - update account data
      const accountRef = doc(db, 'accounts', effectiveUserId);
      await updateDoc(accountRef, {
        ...profileData,
        updatedAt: serverTimestamp()
      });
    } else {
      // Regular client - update their own profile
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating account-scoped user profile:', error);
    throw error;
  }
};
